import React, { useState, useEffect, useContext, useMemo } from 'react';
import { settingsAccess } from '../utils/settingsAccess';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
} from '@mui/material';
import axios from 'axios';
import { ThemeContext } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';
import PayoutScheduleSettings from './components/PayoutScheduleSettings';
import DispatchRadiusCard from './DispatchRadiusCard';

function DashboardSettings() {
  const { currentUser } = useUser();
  // Dispatch radius and dispatch/ETA timing: Org 1 admin-and-up; the look settings below are everyone's
  const isOrg1Admin = useMemo(() => settingsAccess(currentUser).dispatchSettings, [currentUser]);
  const isOwner = isOrg1Admin;

  const defaultColors = {
    jobStatusColors: {
      'Pending': '#FBC02D',
      'Accepted': '#4CAF50',
      'En Route': '#2196F3',
      'On Site': '#FF9800',
      'Completed': '#9C27B0',
    },
    themeColors: {
      light: {
        primary: '#1976d2',
        secondary: '#dc004e',
        background: '#ffffff',
        surface: '#f5f5f5',
        text: '#000000',
      },
      dark: {
        primary: '#90caf9',
        secondary: '#f48fb1',
        background: '#121212',
        surface: '#1e1e1e',
        text: '#ffffff',
      },
    },
  };

  const [settings, setSettings] = useState({
    companyAutoRejectTime: 3,
    driverAutoRejectTime: 2,
    blastRoundTime: 5,
    blastResponseTime: 5,
    spEtaAutoAcceptLimit: 90,
    darkMode: false,
    jobStatusColors: defaultColors.jobStatusColors,
    themeColors: defaultColors.themeColors,
  });
  const [loading, setLoading] = useState(true);
  const { setMode, setCustomColors } = useContext(ThemeContext);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/dashboard-settings', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSettings(res.data);
        
        // Apply theme colors if they exist
        if (res.data.themeColors) {
          setCustomColors(res.data.themeColors);
        }
        
        // Set the correct mode
        if (res.data.darkMode) {
          setMode('dark');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [setMode, setCustomColors]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: newValue,
    }));

    if (name === 'darkMode') {
      const newMode = newValue ? 'dark' : 'light';
      setMode(newMode);
      // Apply custom colors immediately
      setCustomColors(settings.themeColors);
    }
  };

  const handleColorChange = (status, color) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      jobStatusColors: {
        ...prevSettings.jobStatusColors,
        [status]: color,
      },
    }));
  };

  const handleThemeColorChange = (mode, colorType, color) => {
    setSettings((prevSettings) => {
      const newThemeColors = {
        ...prevSettings.themeColors,
        [mode]: {
          ...prevSettings.themeColors[mode],
          [colorType]: color,
        },
      };
      
      // Apply changes immediately to the theme context
      setCustomColors(newThemeColors);
      
      return {
        ...prevSettings,
        themeColors: newThemeColors,
      };
    });
  };

  const handleResetJobStatusColors = () => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      jobStatusColors: defaultColors.jobStatusColors,
    }));
  };

  const handleResetThemeColors = (mode) => {
    setSettings((prevSettings) => {
      const newThemeColors = {
        ...prevSettings.themeColors,
        [mode]: defaultColors.themeColors[mode],
      };
      setCustomColors(newThemeColors);
      return {
        ...prevSettings,
        themeColors: newThemeColors,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/dashboard-settings', settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings.');
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Owners: platform-wide dispatch radius and live-location rules */}
      {isOwner && <DispatchRadiusCard />}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Dashboard Settings</Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {isOrg1Admin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Auto-Reject Time (minutes)"
                  name="companyAutoRejectTime"
                  type="number"
                  value={settings.companyAutoRejectTime}
                  onChange={handleChange}
                />
              </Grid>
            )}
            {isOrg1Admin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Blast Round Time (minutes)"
                  name="blastRoundTime"
                  type="number"
                  value={settings.blastRoundTime ?? 5}
                  onChange={handleChange}
                  helperText="How long each blast dispatch round stays open for providers to accept before the radius expands (or the blast fails after the final round)"
                />
              </Grid>
            )}
            {isOrg1Admin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Blast Failure Response Time (minutes)"
                  name="blastResponseTime"
                  type="number"
                  value={settings.blastResponseTime ?? 5}
                  onChange={handleChange}
                  helperText="How long Org 1 has to respond after a blast dispatch fails before automatic dispatch resumes"
                />
              </Grid>
            )}
            {isOrg1Admin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Driver Auto-Reject Time (minutes)"
                  name="driverAutoRejectTime"
                  type="number"
                  value={settings.driverAutoRejectTime}
                  onChange={handleChange}
                />
              </Grid>
            )}
            {isOrg1Admin && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Max Auto-Approved ETA (minutes)"
                  name="spEtaAutoAcceptLimit"
                  type="number"
                  value={settings.spEtaAutoAcceptLimit ?? 90}
                  onChange={handleChange}
                  helperText="During auto-dispatch, provider ETAs up to this many minutes are approved automatically; longer ETAs are held for your approval. Applies to jobs you create."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={settings.darkMode} onChange={handleChange} name="darkMode" />}
                label="Dark Mode"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1">Job Status Colors</Typography>
                <Button onClick={handleResetJobStatusColors} size="small">Reset</Button>
              </Box>
              {Object.entries(settings.jobStatusColors).map(([status, color]) => (
                <Box key={status} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography sx={{ width: '100px' }}>{status}</Typography>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => handleColorChange(status, e.target.value)}
                  />
                </Box>
              ))}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Theme Colors</Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Light Mode Colors</Typography>
                  <Button onClick={() => handleResetThemeColors('light')} size="small">Reset</Button>
                </Box>
                {Object.entries(settings.themeColors.light).map(([colorType, color]) => (
                  <Box key={`light-${colorType}`} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ width: '120px', textTransform: 'capitalize' }}>{colorType}</Typography>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => handleThemeColorChange('light', colorType, e.target.value)}
                      style={{ marginLeft: '8px', width: '40px', height: '30px' }}
                    />
                  </Box>
                ))}
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Dark Mode Colors</Typography>
                  <Button onClick={() => handleResetThemeColors('dark')} size="small">Reset</Button>
                </Box>
                {Object.entries(settings.themeColors.dark).map(([colorType, color]) => (
                  <Box key={`dark-${colorType}`} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ width: '120px', textTransform: 'capitalize' }}>{colorType}</Typography>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => handleThemeColorChange('dark', colorType, e.target.value)}
                      style={{ marginLeft: '8px', width: '40px', height: '30px' }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary">
                Save Settings
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Owners/RMs: automatic ACH payout scheduling (hides itself for other roles) */}
      <PayoutScheduleSettings />
    </Box>
  );
}

export default DashboardSettings;